import React, { FC, useMemo } from 'react';
import { FormFeedback } from 'reactstrap';
import { LocalSearchProvider } from '../containers/housekeeping/products/utils';
import { Role } from '../models';
import { useTranslation } from '../translations';
import { useFetcher, useUserSafe } from '../utils';
import { ElementSelector } from './ElementSelector';

interface RoleSelectionProps {
  readonly permissionError: string;
  readonly selectedIds: number[];
  readonly setSelectedIds: (vals: number[]) => void;
  readonly disable?: boolean;
  readonly idSuffix?: string;
}

export const RoleSelection: FC<RoleSelectionProps> = ({
  permissionError,
  selectedIds,
  setSelectedIds,
  disable,
  idSuffix,
}) => {
  const user = useUserSafe();
  const canLimitByRole = user.permissions.includes('system:permission:read');
  const rolesFetcher = useFetcher(async () => {
    if (!canLimitByRole) {
      return [];
    }

    return await api.getRoles();
  });

  const { ts } = useTranslation();

  const searchProvider = useMemo(
    () => new LocalSearchProvider(() => rolesFetcher.data ?? []),
    [rolesFetcher.data],
  );

  if (!rolesFetcher.success) {
    return <div>{ts('common.loading')}</div>;
  }

  return (
    <>
      <ElementSelector<Role>
        defaultSelected={selectedIds.map((id) => {
          const entry = rolesFetcher.data.find((t) => t.id === id);
          if (entry) {
            return entry;
          }

          return { id } as Role;
        })}
        disabled={!canLimitByRole || (disable ?? false)}
        id={idSuffix ? `roleSelector${idSuffix}` : 'roleSelector'}
        searchProvider={searchProvider}
        selectionIdsChanged={setSelectedIds}
      />
      {!canLimitByRole && (
        <div>
          <FormFeedback invalid>{permissionError}</FormFeedback>
        </div>
      )}
    </>
  );
};
